if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.currentVariantId = null;
        
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailEvents();
        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      connectedCallback() {
        // Autoplay the featured slide inline on mobile if it's a video
        if (!this.mql.matches) {
          const initialActiveMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
          if (initialActiveMedia) this.playActiveMedia(initialActiveMedia);
        }

        // Initialize variant images on page load
        setTimeout(() => this.initializeVariantImages(), 100);
        
        // Listen for variant changes
        this.variantChangeHandler = this.handleVariantChange.bind(this);
        document.addEventListener('variant:changed', this.variantChangeHandler);
      }

      disconnectedCallback() {
        if (this.variantChangeHandler) {
          document.removeEventListener('variant:changed', this.variantChangeHandler);
        }
      }

      handleVariantChange(event) {
        try {
          const variant = event && event.detail ? event.detail.variant : null;
          if (variant && variant.id) {
            this.updateVariantImages(variant);
          }
        } catch (e) {
          console.warn('Error handling variant change in media gallery:', e);
        }
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (!mediaId) return;
        this.setActiveMedia(mediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail && activeThumbnail.dataset.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        
        const button = thumbnail.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', true);
        }
        
        // Check if thumbnail slider methods exist before calling them
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider && this.elements.thumbnails.slider.scrollTo) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindThumbnailEvents() {
        if (!this.elements.thumbnails) return;
        
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      updateVariantImages(variant) {
        if (!variant || !variant.id) return;

        const variantId = variant.id.toString();
        const hasChangedVariant = this.currentVariantId !== variantId;

        if (hasChangedVariant) {
          // Hide all variant media before showing the new selection
          this.hideAllVariantImages();

          // Reveal media for the selected variant
          this.showVariantImages(variantId);

          this.currentVariantId = variantId;

          // Update thumbnail events to include variant media
          this.bindThumbnailEvents();

          // Reset slider pagination when the available media changes
          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        } else {
          // Ensure current variant media stays visible if re-selected
          this.showVariantImages(variantId);
        }

        this.setActiveVariantMedia(variant);
      }

      hideAllVariantImages() {
        // Hide variant media in main gallery
        this.elements.viewer.querySelectorAll('.variant-media').forEach((media) => {
          media.style.display = 'none';
          media.classList.add('variant-media--hidden');
        });
        
        // Hide variant thumbnails
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('.variant-thumbnail').forEach((thumb) => {
            thumb.style.display = 'none';
            thumb.classList.add('variant-thumbnail--hidden');
          });
        }
      }

      showVariantImages(variantId) {
        if (!variantId) return;
        
        // Show variant media for selected variant
        this.elements.viewer.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach((media) => {
          if (media.classList.contains('variant-media')) {
            media.style.display = '';
            media.classList.remove('variant-media--hidden');
            
            // Handle lazy loading for variant images
            const img = media.querySelector('img[loading="lazy"]');
            if (img && !img.classList.contains('loaded')) {
              img.onload = () => {
                img.classList.add('loaded');
              };
              // Trigger loading if not already loaded
              if (img.complete) {
                img.classList.add('loaded');
              }
            }
          }
        });
        
        // Show variant thumbnails for selected variant
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach((thumb) => {
            if (thumb.classList.contains('variant-thumbnail')) {
              thumb.style.display = '';
              thumb.classList.remove('variant-thumbnail--hidden');
            }
          });
        }
      }

      setActiveVariantMedia(variant) {
        if (!variant || !variant.id || !variant.featured_media || !variant.featured_media.id) return;

        const variantId = variant.id.toString();
        let featuredMediaElement = null;

        if (this.elements.viewer) {
          featuredMediaElement = this.elements.viewer.querySelector(
            `[data-variant-featured="true"][data-variant-id="${variantId}"]`
          );

          if (!featuredMediaElement) {
            featuredMediaElement = this.elements.viewer.querySelector(
              `[data-media-id$="-${variant.featured_media.id}"]`
            );
          }
        }

        if (!featuredMediaElement || !featuredMediaElement.dataset.mediaId) return;

        this.setActiveMedia(featuredMediaElement.dataset.mediaId, false);
      }

      initializeVariantImages() {
        // Get initial variant from page state
        const productInfoElement = document.querySelector('product-info');
        if (productInfoElement) {
          const variantSelect = productInfoElement.querySelector('variant-selects [data-selected-variant]');
          if (variantSelect && variantSelect.innerHTML) {
            try {
              const selectedVariant = JSON.parse(variantSelect.innerHTML);
              if (selectedVariant && selectedVariant.id) {
                this.updateVariantImages(selectedVariant);
              }
            } catch (e) {
              console.warn('Could not parse selected variant:', e);
            }
          }
        }
      }
    }
  );
}
